import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, Toolbar, Button, Typography, Tooltip, Menu, MenuItem, Checkbox, IconButton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';



// Define reusable styles for columns
const columnStyles = {
  userQuery: {
    maxWidth: '300px', // Fixed maximum width
    minWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal', // Allow line breaks
    wordWrap: 'break-word', // Break long words
    maxHeight: '100px', // Set max height
    overflowY: 'auto', // Enable vertical scroll inside the cell
  },
  tenantModel: {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  responseCell: {
    maxHeight: '100px', // Limit height
    overflowY: 'auto', // Enable scrolling inside cell
    maxWidth: '500px',
    whiteSpace: 'normal',
  },
  stickyHeader: {
    fontWeight: 'bold',
    position: 'sticky',
    top: 0,
    background: '#f1f1f1',
    zIndex: 1,
  },
};

const ResultsTable = ({
  results,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  onDownloadCSV,
}) => {
  const columnNames = results.length > 0
  ? Object.keys(results[0]).filter((key) => key !== "_originalRow")
  : [];

  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(new Set(columnNames));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleColumn = (column) => {
    const newSet = new Set(visibleColumns);
    if (newSet.has(column)) {
      newSet.delete(column);
    } else {
      newSet.add(column);
    }
    setVisibleColumns(newSet);
  };

const visibleColumnNames = columnNames.filter((col) => visibleColumns.has(col));
return (
  <div style={{ overflowX: 'auto', maxWidth: 'calc(100vw - 280px)' }}>
    <div style={{ minWidth: '1200px' }}>
      {/* Toolbar for Download Button */}
      <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Results</Typography>
        <div>
          <IconButton onClick={handleMenuOpen} size="small">
            <ViewColumnIcon />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            {columnNames.map((col) => (
              <MenuItem key={col} onClick={() => toggleColumn(col)}>
                <Checkbox checked={visibleColumns.has(col)} />
                <Typography variant="body2">{col.replace(/_/g, ' ')}</Typography>
              </MenuItem>
            ))}
          </Menu>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={onDownloadCSV}
            sx={{ ml: 2 }}
          >
            Download as CSV
          </Button>
        </div>
      </Toolbar>

      {/* Table Container */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '500px',
          overflowX: 'auto',
          overflowY: 'auto',
        }}
      >
        <Table stickyHeader sx={{ minWidth: '1200px' }}>
          <TableHead>
            <TableRow>
              {visibleColumnNames.map((col) => (
                <TableCell
                  key={col}
                  style={{
                    ...columnStyles.stickyHeader,
                    ...(columnStyles[col] || {}),
                  }}
                >
                  {col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
              <TableRow
                key={index}
                hover
                onClick={() => onRowClick(row._originalRow || row)}
                style={{ cursor: 'pointer' }}
              >
                {visibleColumnNames.map((col) => (
                  <TableCell key={col} style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip
                      title={
                        <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                          {typeof row[col] === 'object' ? JSON.stringify(row[col], null, 2) : String(row[col])}
                        </pre>
                      }
                      arrow
                      placement="top-start"
                    >
                      <span
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'block',
                          maxHeight: '6em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal',
                          lineHeight: '1.5em',
                        }}
                      >
                        {typeof row[col] === 'object' ? JSON.stringify(row[col]) : row[col]}
                      </span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={results.length}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </div>
  </div>
);
};

export default ResultsTable;